/**
 * Prontidão do pedido: responder a uma clarificação NUNCA gera.
 *
 * Máquina de estados do pedido:
 *
 *   clarifying → ready_to_generate → generating → published | failed
 *
 * Regra de produto: uma resposta de clarificação (aeroporto, hotel, saída,
 * duração, homónimo, "sem logística") apenas atualiza estado local. A geração
 * paga — IA, Places, Routes, Photos, crédito, overhead e `generationId` — só
 * pode começar por clique explícito no botão final visível.
 */

export type RequestPhase =
  | "idle"
  | "clarifying"
  | "ready_to_generate"
  | "generating"
  | "published"
  | "failed";

/** De onde veio esta submissão. */
export type SubmitTrigger =
  /** A pessoa escreveu o pedido e carregou em enviar/gerar. */
  | "user_submit"
  /** Resposta a uma pergunta de clarificação (chip, input, Enter, skip). */
  | "clarification_answer"
  /** Clique explícito no botão final "Criar itinerário". */
  | "explicit_generate";

export type GenerationStartDecision = {
  /**
   * `generate` — pode arrancar trabalho pago;
   * `hold` — apenas atualiza estado, sem overlay nem fornecedor.
   */
  action: "generate" | "hold";
  phase: RequestPhase;
  /** Motivo legível para trace/testes. */
  reason:
    | "explicit_click"
    | "user_submit"
    | "clarification_answer_never_generates"
    | "already_generating";
};

/**
 * Decide se uma submissão pode iniciar geração paga. Pura: sem rede, sem
 * estado global, sem efeitos.
 */
export function decideGenerationStart(input: {
  trigger: SubmitTrigger;
  /** Já existe uma geração em curso (protege contra duplo clique). */
  alreadyGenerating: boolean;
}): GenerationStartDecision {
  if (input.alreadyGenerating) {
    return { action: "hold", phase: "generating", reason: "already_generating" };
  }
  if (input.trigger === "clarification_answer") {
    return {
      action: "hold",
      phase: "ready_to_generate",
      reason: "clarification_answer_never_generates",
    };
  }
  return {
    action: "generate",
    phase: "generating",
    reason: input.trigger === "explicit_generate" ? "explicit_click" : "user_submit",
  };
}

/**
 * Fase depois de guardar uma resposta de clarificação: continua a perguntar
 * enquanto faltar informação obrigatória; caso contrário fica à espera do
 * clique final.
 */
export function phaseAfterClarificationAnswer(hasPendingQuestion: boolean): RequestPhase {
  return hasPendingQuestion ? "clarifying" : "ready_to_generate";
}

/** Texto do cartão final (uma linha por idioma, sem depender do produto). */
export function readyToGenerateCopy(lang: string): { title: string; cta: string; credit: string } {
  const table: Record<string, { title: string; cta: string; credit: string }> = {
    pt: {
      title: "Logística concluída.",
      cta: "Criar itinerário",
      credit: "Só usamos crédito quando carregares em “Criar itinerário”.",
    },
    en: {
      title: "Logistics complete.",
      cta: "Create itinerary",
      credit: "Credit is only used when you tap “Create itinerary”.",
    },
    es: {
      title: "Logística completa.",
      cta: "Crear itinerario",
      credit: "Solo usamos crédito al pulsar “Crear itinerario”.",
    },
    fr: {
      title: "Logistique terminée.",
      cta: "Créer l'itinéraire",
      credit: "Le crédit n'est utilisé qu'en appuyant sur « Créer l'itinéraire ».",
    },
    it: {
      title: "Logistica completata.",
      cta: "Crea itinerario",
      credit: "Il credito si usa solo premendo “Crea itinerario”.",
    },
    de: {
      title: "Logistik abgeschlossen.",
      cta: "Reiseplan erstellen",
      credit: "Guthaben wird erst mit „Reiseplan erstellen“ verbraucht.",
    },
    ja: {
      title: "移動情報がそろいました。",
      cta: "旅程を作成",
      credit: "「旅程を作成」を押したときだけクレジットを使います。",
    },
  };
  return table[lang] ?? table["en"]!;
}
